import { Button, Form, Offcanvas } from 'react-bootstrap';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import RequestStatusBadge from '../RequestStatusBadge';
import type { SupplierRequestFilterState } from '../model/types';
import {
    buildStatusSelectStyles,
    toStatusOptions,
    toVendorOptions,
    type StatusOption,
} from './helper';

interface OffFilterProps {
    show: boolean;
    draftFilter: SupplierRequestFilterState;
    setDraftFilter: React.Dispatch<React.SetStateAction<SupplierRequestFilterState>>;
    onApply: () => void;
    onReset: () => void;
    onHide: () => void;
}

const formatStatusOption = (option: StatusOption) => (
    <RequestStatusBadge status={option.value} />
);

const OffFilter = ({
    show,
    draftFilter,
    setDraftFilter,
    onApply,
    onReset,
    onHide,
}: OffFilterProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { settings } = useSettings();
    const { darkTheme } = useTheme();
    const [selectTheme, setSelectTheme] = useState(darkTheme ? selectStylesDark : selectStylesLigth);

    const vendorOptions = useMemo(
        () => toVendorOptions(settings?.DIRS?.Vendor),
        [settings?.DIRS?.Vendor],
    );

    const statusOptions = useMemo(() => toStatusOptions(t), [t]);

    const statusSelectStyles = useMemo(
        () => buildStatusSelectStyles(selectTheme),
        [selectTheme],
    );

    useEffect(() => {
        setSelectTheme(darkTheme ? selectStylesDark : selectStylesLigth);
    }, [darkTheme]);

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('supplierRequests.filter.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-filter-form d-flex flex-column h-100">
                    <div className="off-filter-content px-3">
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('supplierRequests.filter.labelVendors')}
                            </label>
                            <Select
                                options={vendorOptions}
                                styles={selectTheme}
                                value={vendorOptions.filter((v) =>
                                    draftFilter.vendors.includes(v.value),
                                )}
                                onChange={(value) => {
                                    const selected = Array.isArray(value) ? value : [];
                                    setDraftFilter((prev) => ({
                                        ...prev,
                                        vendors: selected.map((v) => v.value),
                                    }));
                                }}
                                isMulti
                                placeholder={t('supplierRequests.filter.placeholderVendors')}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('supplierRequests.filter.labelStatuses')}
                            </label>
                            <Select<StatusOption, true>
                                options={statusOptions}
                                styles={statusSelectStyles}
                                value={statusOptions.filter((s) =>
                                    draftFilter.statuses.includes(s.value),
                                )}
                                onChange={(value) => {
                                    const selected = Array.isArray(value) ? value : [];
                                    setDraftFilter((prev) => ({
                                        ...prev,
                                        statuses: selected.map((s) => Number(s.value)),
                                    }));
                                }}
                                formatOptionLabel={formatStatusOption}
                                isMulti
                                isSearchable={false}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                placeholder={t('supplierRequests.filter.placeholderStatuses')}
                            />
                        </Form.Group>
                    </div>

                    <div className="p-3 offcanvas-footer off-filter-footer">
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={onReset}>
                                {t('common:buttons.reset')}
                            </Button>
                            <Button variant="info" className="min100px" onClick={onApply}>
                                {t('common:buttons.apply')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffFilter;
